import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import {
	Button,
	Grid,
	Typography,
	useTheme,
} from "@mui/material";

import {
	setAllowNext,
	setAllowPrevious,
} from "../../../store/Navigation/NavSlice";
import { RootState } from "../../../store";
import { SurveySectionProps } from "./SurveySectionComponent";

import "./SurveySection.scss";
import { Link } from "react-router-dom";
import { ResetSurveyStatus } from "../../../pages/home/ResetSurveyStatus";
import { FormattedPhrase } from "../../../context/FormattedPhrase";
import { pdf } from "@react-pdf/renderer";
import SurveyResponsePDF from "../../pdfGenerator/PDFGenerator";
import { SurveyAnswer, SurveyResponse, Question, SurveyScale, Option } from "../../../@types/surveys.d";
import cclLogo from "../../../assets/images/cclLogoChrome.png";
import cclBranding from "../../../assets/images/ccl-logo.png";
import { SurveyStatus } from "../../../@types/raters.d";
import moment from "moment";
import { useTranslationContext } from "../../../context/TranslationContext";
import { getAnalytics, logEvent } from "firebase/analytics";

type ProcessedQuestion = Question & { scale: SurveyScale };

export const SurveyCompleteComponent: React.FC<SurveySectionProps> = ({
	surveySection,
}) => {
	const theme = useTheme();
	const { showSectionSubheading } = useSelector((state: RootState) => state.navigation);
	const { activeSurvey, localSurveyLanguage, raterInformationForRaterSurvey, programRaterStatuses, dateFormat, userIdForAnalytics } = useSelector((state: RootState) => state.user);
	const [processedSections, setProcessedSections] = useState<any>([]);
	const { getFormattedPhrase } = useTranslationContext();	
	const [reportTextData, setReportTextData] = useState<any>([]);

	useEffect(() => {
		setAllowNext(false);
		setAllowPrevious(true);
		if (activeSurvey && activeSurvey.surveyDocument.survey.surveyTypeId === 1) {
			const sections = processSurveyData(activeSurvey);
			setProcessedSections(sections);
			const matchingStatus = programRaterStatuses?.surveyStatuses?.find(
				(status: SurveyStatus) => status.instrument?.instrumentId === activeSurvey.surveyDocument.survey?.instrumentId
			);
        
			const completedDateStamp = matchingStatus?.assessmentCompletedDate ?? getLatestAnswerDate(activeSurvey.surveyAnswers);
			const completedDate = moment(completedDateStamp).format(dateFormat);
			
			setReportTextData([
				`${getFormattedPhrase("surveys.dateCompleted",null,localSurveyLanguage)}: ${completedDate}`,
				getFormattedPhrase("raterReport.summary",null,localSurveyLanguage),
				getFormattedPhrase("raterReport.summary.text",null,localSurveyLanguage),
				getFormattedPhrase("raterReport.pageXofY",null,localSurveyLanguage)
			]);
		}
	}, [surveySection, activeSurvey]);

	const getLatestAnswerDate = (answers: SurveyAnswer[]) => {
		if (!answers || answers.length === 0) return Date.now();

		const latestAnswer = answers.reduce((latest, answer) => {
			return answer.answeredTimeMs > latest.answeredTimeMs ? answer : latest;
		});

		return latestAnswer?.answeredTimeMs || Date.now();
	}

	const processSurveyData = (surveyData: SurveyResponse) => {
		if (!surveyData || !surveyData.surveyDocument || !surveyData.surveyAnswers) {
			return [];
		}

		const survey = surveyData.surveyDocument.survey;
		const surveyAnswers = surveyData.surveyAnswers;
		const sections = survey.surveySections.filter((ss) => ss.component !== "SurveyCompleteComponent" && ss.component !== "SurveyReviewQuestionsComponent") || [];

		const answerMap: Record<number, string> = {};
		surveyAnswers?.forEach(answer => {
			answerMap[answer.formItemId] = answer.answer;
		});

		const processedSections = sections.map(section => {
			const processedGroups = (section.questionGroups || []).map(group => {
				const processedQuestions = (group.questions || []).map(question => {
					const processedQuestion: ProcessedQuestion = {
						...question,
						answer: answerMap[question.formItemId] ?? "Not answered",
						scale: group.surveyScale
					};

					if (question.dependentOption?.formItemId) {
						processedQuestion.dependentOption = {
							...question.dependentOption,
							answer: answerMap[question.dependentOption.formItemId] ?? null
						};
					}

					if (question.options?.some((option) => !!option.formItemId)) {
						processedQuestion.options = question.options.map(option => {
							const processedOption: Option = { ...option };
							if (option.formItemId) {
								processedOption.answer = answerMap[option.formItemId] ?? null;
							}
							if (option.dependentFormItemSequenceNumber && option.dependentOption?.formItemId) {
								processedOption.dependentOption = {
									...option.dependentOption,
									answer: answerMap[option.dependentOption.formItemId] ?? null
								};
							}
							return processedOption;
						});
					}

					return processedQuestion;
				});

				return {
					...group,
					questions: processedQuestions
				};
			});

			return {
				...section,
				questionGroups: processedGroups
			};
		});

		return processedSections;
	};

	const handleDownloadPDF = async () => {
		logEvent(getAnalytics(), "[BUTTON] Download a copy of rater responses", { "gc_analytics_id": userIdForAnalytics });
		const blob = await pdf(
			<SurveyResponsePDF
				info={raterInformationForRaterSurvey}
				surveyData={activeSurvey}
				processedSections={processedSections}
				logos={{
					topLeft: cclLogo,
					bottomRight: cclBranding
				}}
				translatedText={reportTextData}
			/>
		).toBlob();

		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `Rater_Survey_for_${raterInformationForRaterSurvey.participant.firstName}_${raterInformationForRaterSurvey.participant.lastName}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		//a.s.: prevent any caching to avoid report misuse
		URL.revokeObjectURL(url);
	};

	return (
		<Fragment>
			<ResetSurveyStatus completed={true} />
			<Grid item xs={12} sm={12} md={12} className="rounded-card">
				<Grid item md={12}>
					<h1>{activeSurvey.surveyDocument?.survey?.name}</h1>
				</Grid>
				<Grid item md={12}>
					<h3>{surveySection?.heading}</h3>
				</Grid>
				<Grid item md={12}>
					{showSectionSubheading && surveySection?.subheadings && (
						<div className="section-subheading">
							{surveySection?.subheadings?.map((item, index) => {
								return (
									<Typography
										gutterBottom
										variant="h6"
										component="div"
										className="p-3 text-left question-title"
										key={index}
									>
										{item}
									</Typography>
								);
							})}
						</div>
					)}
				</Grid>
				{surveySection?.instructions?.map((item, index) => {
					return (
						<Grid item xs={12} key={index}>
							<ReactMarkdown rehypePlugins={[rehypeRaw]}>{item}</ReactMarkdown>
						</Grid>
					);
				})}
				<div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
					<Button
						sx={{
							backgroundColor: theme.palette.secondary.contrastText,
							borderColor: theme.palette.primary.dark,
							color: theme.palette.primary.dark,
							borderRadius: 100,
						}}
						variant="outlined"
					>
						<Link to="/" style={{ textDecoration: "none" }}>
							<FormattedPhrase id="dashboard.footer.returnToAP" language={localSurveyLanguage} />
						</Link>
					</Button>
					{
						//a.s.: Per 75627, only Rater surveys are available for downloading
						activeSurvey.surveyDocument.survey.surveyTypeId === 1 &&
						<Button
							onClick={handleDownloadPDF}
							sx={{
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.primary.contrastText,
								borderRadius: 100,
								'&:hover': {
									backgroundColor: theme.palette.primary.dark,
								}
							}}
							variant="contained"
						>
							{getFormattedPhrase("raterReport.downloadButton",null,localSurveyLanguage)}
						</Button>
					}
				</div>
			</Grid>
		</Fragment>
	);
};